import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Popconfirm, Tabs } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import api from "../interceptors/api";

const STORAGE_KEY = "requestInspectorBin";
const POLL_INTERVAL_MS = 2000;

// Mirrors the backend's inline-safe list: only these get <img> thumbnails.
const THUMBNAIL_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
  "image/x-icon",
  "image/vnd.microsoft.icon",
]);

const METHOD_STYLES = {
  GET: "bg-emerald-100 text-emerald-800 border-emerald-200",
  POST: "bg-indigo-100 text-indigo-800 border-indigo-200",
  PUT: "bg-amber-100 text-amber-800 border-amber-200",
  PATCH: "bg-orange-100 text-orange-800 border-orange-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
  HEAD: "bg-gray-100 text-gray-700 border-gray-200",
  OPTIONS: "bg-sky-100 text-sky-800 border-sky-200",
};

const loadStoredBin = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const bin = JSON.parse(raw);
    if (!bin?.id || !bin?.viewToken || !bin?.expiresAt) return null;
    if (new Date(bin.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return bin;
  } catch {
    return null;
  }
};

const formatBytes = (n) => {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const prettyBody = (body) => {
  if (body == null || body === "") return "";
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

const fileUrl = (file, viewToken, download = false) =>
  `${file.url}?t=${encodeURIComponent(viewToken)}${download ? "&download=1" : ""}`;

const buildExamples = (url) => [
  { key: "curl_get", labelKey: "ri_example_curl_get", code: `curl "${url}/orders?status=paid&page=1"` },
  {
    key: "curl_json",
    labelKey: "ri_example_curl_json",
    code: `curl -X POST "${url}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"event":"order.created","id":42}'`,
  },
  {
    key: "curl_image",
    labelKey: "ri_example_curl_image",
    code: `curl -X POST "${url}" \\\n  -F "file=@photo.jpg" \\\n  -F "caption=Hello"`,
  },
  {
    key: "curl_raw",
    labelKey: "ri_example_curl_raw",
    code: `curl -X POST "${url}" \\\n  -H "Content-Type: image/jpeg" \\\n  --data-binary @photo.jpg`,
  },
  {
    key: "fetch",
    labelKey: "ri_example_fetch",
    code: `await fetch("${url}", {\n  method: "POST",\n  headers: { "Content-Type": "application/json" },\n  body: JSON.stringify({ event: "order.created", id: 42 }),\n});`,
  },
];

const KeyValueTable = ({ rows, emptyText }) => {
  if (!rows.length) return <p className="text-sm text-gray-500 italic">{emptyText}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([name, value], i) => (
            <tr key={`${name}-${i}`} className="border-t border-gray-100 first:border-t-0">
              <td className="py-1 pr-4 align-top font-mono text-indigo-700 whitespace-nowrap">{name}</td>
              <td className="py-1 font-mono text-gray-800 break-all">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-1">
    <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">{title}</div>
    {children}
  </div>
);

const RequestDetails = ({ request, viewToken, t, copy }) => {
  const queryRows = Object.entries(request.query || {}).flatMap(([k, v]) =>
    Array.isArray(v) ? v.map((x) => [k, x]) : [[k, v]]
  );
  const headerRows = (request.headers || []).map((h) => [h.name, h.value]);
  const formRows = (request.formFields || []).map((f) => [f.name, f.value]);
  const body = prettyBody(request.body);

  return (
    <div className="border-t border-gray-200 p-4 flex flex-col gap-4 bg-gray-50">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
        <div>
          <span className="text-gray-500">{t("ri_field_ip")}: </span>
          <span className="font-mono">{request.ip || "—"}</span>
        </div>
        <div>
          <span className="text-gray-500">{t("ri_field_content_type")}: </span>
          <span className="font-mono break-all">{request.contentType || "—"}</span>
        </div>
        <div>
          <span className="text-gray-500">{t("ri_field_received")}: </span>
          <span>{new Date(request.receivedAt).toLocaleString()}</span>
        </div>
      </div>

      <Section title={t("ri_section_query")}>
        <KeyValueTable rows={queryRows} emptyText={t("ri_empty_query")} />
      </Section>

      <Section title={t("ri_section_headers")}>
        <KeyValueTable rows={headerRows} emptyText={t("ri_empty_headers")} />
      </Section>

      {formRows.length > 0 && (
        <Section title={t("ri_section_form")}>
          <KeyValueTable rows={formRows} emptyText="" />
        </Section>
      )}

      {request.files?.length > 0 && (
        <Section title={t("ri_section_files")}>
          <div className="flex flex-col gap-2">
            {request.files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-2"
              >
                {THUMBNAIL_TYPES.has((file.type || "").split(";")[0].trim().toLowerCase()) ? (
                  <a href={fileUrl(file, viewToken)} target="_blank" rel="noopener noreferrer">
                    <img
                      src={fileUrl(file, viewToken)}
                      alt={file.name || file.field || t("ri_file_untitled")}
                      className="w-20 h-20 object-cover rounded border border-gray-200 bg-gray-100"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="w-20 h-20 flex items-center justify-center rounded border border-gray-200 bg-gray-100 text-xs text-gray-500 font-mono">
                    {(file.type || "").split("/")[1]?.slice(0, 8) || "file"}
                  </div>
                )}
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-medium break-all">
                    {file.name || t("ri_file_untitled")}
                  </div>
                  <div className="text-gray-500 font-mono text-xs break-all">
                    {t("ri_file_field")}: {file.field || "—"} · {file.type} · {formatBytes(file.size)}
                  </div>
                </div>
                <a
                  href={fileUrl(file, viewToken, true)}
                  className="text-sm text-indigo-700 hover:underline whitespace-nowrap"
                >
                  {t("ri_download")}
                </a>
              </div>
            ))}
          </div>
        </Section>
      )}

      {body !== "" && (
        <Section title={t("ri_section_body")}>
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap break-all">
              {body}
            </pre>
            <div className="absolute top-2 right-2">
              <StandardButton
                onClick={() => copy(request.body, t("ri_section_body"))}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("copy_button")}
              </StandardButton>
            </div>
          </div>
          {request.bodyTruncated && (
            <p className="text-xs text-amber-700">{t("ri_body_truncated")}</p>
          )}
        </Section>
      )}
    </div>
  );
};

const RequestInspector = () => {
  const { t } = useTranslation();
  const [bin, setBin] = useState(loadStoredBin);
  const [requests, setRequests] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [creating, setCreating] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [visible, setVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState === "visible"
  );
  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);

  const expiresAtMs = bin ? new Date(bin.expiresAt).getTime() : 0;
  const remainingMs = bin ? expiresAtMs - now : 0;
  const expired = Boolean(bin) && remainingMs <= 0;

  const copy = useCallback(
    async (text, label) => {
      try {
        await navigator.clipboard.writeText(text || "");
        toast.success(label ? `${label} — ${t("copy_success")}` : t("copy_success"));
      } catch {
        toast.error(t("failed_to_copy"));
      }
    },
    [t]
  );

  const resetLocal = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    cursorRef.current = null;
    setBin(null);
    setRequests([]);
    setExpanded({});
  }, []);

  const deleteRemote = async (target) => {
    if (!target) return;
    try {
      await api.delete(`/request-inspector/bins/${target.id}`, {
        headers: { "x-view-token": target.viewToken },
      });
    } catch {
      // Already expired or gone — nothing to clean up.
    }
  };

  const createBin = async () => {
    setCreating(true);
    const previous = bin;
    try {
      const { data } = await api.post("/request-inspector/bins");
      const next = {
        id: data.id,
        viewToken: data.viewToken,
        expiresAt: data.expiresAt,
        endpointUrl: data.endpointUrl,
        maxRequests: data.maxRequests,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      cursorRef.current = null;
      setRequests([]);
      setExpanded({});
      setBin(next);
      setNow(Date.now());
      if (previous) deleteRemote(previous);
      toast.success(t("ri_created"));
    } catch (err) {
      if (err.response?.status === 429) toast.error(t("ri_error_rate_limited"));
      else toast.error(t("ri_error_create"));
    } finally {
      setCreating(false);
    }
  };

  const clearBin = async () => {
    const target = bin;
    resetLocal();
    await deleteRemote(target);
    toast.success(t("ri_cleared"));
  };

  const poll = useCallback(async () => {
    if (!bin || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const { data } = await api.get(`/request-inspector/bins/${bin.id}/requests`, {
        headers: { "x-view-token": bin.viewToken },
        params: cursorRef.current ? { after: cursorRef.current } : undefined,
      });
      if (data.cursor) cursorRef.current = data.cursor;
      if (data.requests?.length) {
        setRequests((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          const fresh = data.requests.filter((r) => !seen.has(r.id));
          return fresh.length ? [...fresh, ...prev] : prev;
        });
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 403) {
        toast.error(t("ri_error_expired"));
        resetLocal();
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [bin, resetLocal, t]);

  // Countdown tick.
  useEffect(() => {
    if (!bin) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [bin]);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Poll every 2 s while the tab is visible and the endpoint is alive.
  const polling = Boolean(bin) && visible && !expired;
  useEffect(() => {
    if (!polling) return undefined;
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [polling, poll]);

  const examples = useMemo(() => (bin ? buildExamples(bin.endpointUrl) : []), [bin]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">{t("ri_title")}</h1>
            <p className="text-gray-600">{t("ri_intro")}</p>

            {!bin && (
              <div className="flex flex-col items-start gap-2">
                <StandardButton onClick={createBin} loading={creating}>
                  {t("ri_create")}
                </StandardButton>
                <p className="text-xs text-gray-500">{t("ri_create_hint")}</p>
              </div>
            )}

            {bin && (
              <>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
                      {t("ri_endpoint_label")}
                    </div>
                    <div
                      className={`text-sm font-medium ${expired ? "text-red-700" : "text-gray-700"}`}
                      role="timer"
                      aria-live="off"
                    >
                      {expired
                        ? t("ri_expired")
                        : t("ri_expires_in", { time: formatCountdown(remainingMs) })}
                    </div>
                  </div>
                  <div className="flex gap-2 items-stretch flex-wrap sm:flex-nowrap">
                    <input
                      readOnly
                      value={bin.endpointUrl}
                      onFocus={(e) => e.target.select()}
                      aria-label={t("ri_endpoint_label")}
                      className="flex-1 min-w-0 p-2 border border-indigo-200 rounded-lg font-mono text-sm bg-white"
                    />
                    <StandardButton onClick={() => copy(bin.endpointUrl, t("ri_endpoint_label"))}>
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <StandardButton
                      onClick={createBin}
                      loading={creating}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("ri_new_endpoint")}
                    </StandardButton>
                    <Popconfirm
                      title={t("ri_clear_confirm")}
                      onConfirm={clearBin}
                      okText={t("ri_clear")}
                      cancelText={t("ri_cancel")}
                    >
                      <StandardButton type="secondary" variant="outlined" size="small" danger>
                        {t("ri_clear")}
                      </StandardButton>
                    </Popconfirm>
                    <span className="text-xs text-gray-500">
                      {polling
                        ? t("ri_status_live")
                        : expired
                        ? t("ri_status_stopped")
                        : t("ri_status_paused")}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold text-gray-800">{t("ri_examples_title")}</h2>
                  <Tabs
                    size="small"
                    items={examples.map((ex) => ({
                      key: ex.key,
                      label: t(ex.labelKey),
                      children: (
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-100 text-xs p-3 pr-20 rounded-lg overflow-x-auto whitespace-pre">
                            {ex.code}
                          </pre>
                          <div className="absolute top-2 right-2">
                            <StandardButton
                              onClick={() => copy(ex.code, t(ex.labelKey))}
                              type="secondary"
                              variant="outlined"
                              size="small"
                            >
                              {t("copy_button")}
                            </StandardButton>
                          </div>
                        </div>
                      ),
                    }))}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {t("ri_requests_title", { count: requests.length })}
                    </h2>
                    <span className="text-xs text-gray-500">
                      {t("ri_requests_limit", { max: bin.maxRequests || 200 })}
                    </span>
                  </div>

                  {requests.length === 0 && (
                    <p className="text-sm text-gray-500 italic">{t("ri_waiting")}</p>
                  )}

                  <ul className="flex flex-col gap-2">
                    {requests.map((r) => {
                      const open = Boolean(expanded[r.id]);
                      return (
                        <li key={r.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggle(r.id)}
                            aria-expanded={open}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-indigo-50 transition"
                          >
                            <span
                              className={`inline-block min-w-[4.5rem] text-center text-xs font-bold border rounded px-2 py-0.5 ${
                                METHOD_STYLES[r.method] || METHOD_STYLES.HEAD
                              }`}
                            >
                              {r.method}
                            </span>
                            <span className="flex-1 min-w-0 font-mono text-sm truncate">
                              {r.path}
                              {r.queryString ? `?${r.queryString}` : ""}
                            </span>
                            {r.files?.length > 0 && (
                              <span className="text-xs text-indigo-700 whitespace-nowrap">
                                {t("ri_files_count", { count: r.files.length })}
                              </span>
                            )}
                            <span className="text-xs text-gray-500 whitespace-nowrap">{formatBytes(r.size)}</span>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {new Date(r.receivedAt).toLocaleTimeString()}
                            </span>
                          </button>
                          {open && <RequestDetails request={r} viewToken={bin.viewToken} t={t} copy={copy} />}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2 text-xs text-gray-500 leading-relaxed flex flex-col gap-1">
              <p className="font-medium text-gray-600">{t("ri_how_title")}</p>
              <p>{t("ri_how_body")}</p>
              <p>{t("ri_limits_note")}</p>
              <p>{t("ri_privacy_note")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RequestInspector;
